// The composed half of the score: tonality, form, and the register budget.
//
// music.ts owns the Web Audio graph and the scheduler; everything here is
// arithmetic, so the arrangement itself can be unit-tested. The register bands
// are the load-bearing part -- they are what keeps the music out of the
// frequencies the sound effects already own, which is the whole reason the
// music can be added without washing them out.
import { LADDER_BASE_HZ, clamp01 } from "./audioParams";
import { mulberry32 } from "./rng";

// ---------------------------------------------------------------------------
// Tonality. The score is in just intonation off C, the same tuning the pickup
// ladder uses, because that is the sound the player hears constantly while the
// music is playing. The finish fanfare is tempered and sits 13.7 cents away on
// the third -- which is why the score resolves to an open fifth and never
// sustains a third under it. See RESOLVE_TONES.
export const MUSIC_ROOT_HZ = LADDER_BASE_HZ / 8; // C2, three octaves under the ladder

const MAJOR = [1, 5 / 4, 3 / 2] as const;
const MINOR = [1, 6 / 5, 3 / 2] as const;

export type Chord = { name: string; root: number; tones: readonly number[] };

// Only chords whose thirds and fifths are exact against C. D minor is absent
// on purpose: its third against a just C is 32/27, the Pythagorean comma's
// wolf, and it beats audibly under a sustained pad.
export const CHORDS = {
  C: { name: "C", root: 1, tones: MAJOR },
  Em: { name: "Em", root: 5 / 4, tones: MINOR },
  F: { name: "F", root: 4 / 3, tones: MAJOR },
  G: { name: "G", root: 3 / 2, tones: MAJOR },
  Am: { name: "Am", root: 5 / 3, tones: MINOR },
} as const satisfies Record<string, Chord>;

export type ChordName = keyof typeof CHORDS;

// The finish lands here: root, fifth, octave, voiced from C2 up. Two things are
// deliberate.
//
// No third. The pickup ladder tunes E justly and the finish fanfare tunes it
// equally, 13.7 cents apart, and a sustained third would beat against one of
// them right through the payoff. Root, fifth and octave agree with the
// tempered reading to within two cents, so there is nothing left to beat.
//
// Always C, whatever the theme was in. Two of the five arrangements sit in A
// minor, and an A-E fifth would put that same contested E back into the chord;
// C-G-C is a good resolution out of A minor anyway, and it is the only voicing
// that is exact against the fanfare in every theme.
export const RESOLVE_TONES = [1, 3 / 2, 2] as const;
export const RESOLVE_HZ: readonly number[] = RESOLVE_TONES.map((r) => MUSIC_ROOT_HZ * r * 2);

// ---------------------------------------------------------------------------
// The register budget.
//
// These are the bands the score is allowed to put fundamentals in, chosen from
// what audio.ts already occupies. REWARD_BAND is the exclusion zone: the pickup
// ladder (523-1308Hz), the finish arpeggio (392-1318Hz), the airborne whistle
// at 1300 and the tree crack at 1100 all live there, and they are the sounds
// that have to stay legible. Nothing in the score puts a fundamental inside it.
//
// Harmonics are a separate problem -- a sawtooth at 130Hz has partials all the
// way up -- and they are handled in the graph, by the peaking cut on the music
// bus and by per-voice lowpasses. This module can only promise the fundamentals.
export const REWARD_BAND = [500, 1400] as const;

export const BANDS = {
  sub: [55, 110],
  bass: [65, 200],
  stab: [250, 500],
  pluck: [220, 440],
  hook: [1400, 2800],
} as const;

// The fixed-pitch voices: drum fundamentals and the noise bands they are built
// from. They live here rather than in music.ts so that the whole register
// budget -- pitched and unpitched -- is asserted in one place.
export const PERCUSSION = {
  kick: { from: 112, to: 41, click: 1800 },
  snare: { from: 192, to: 148, noise: 4200 },
  hat: 8200,
  shaker: 9500,
  cymbal: 6000,
  riser: { from: 70, to: 330 },
} as const;

export type Band = readonly [number, number];

// Octave-shift a frequency into a band. Every band above spans at least an
// octave, so the result always lands inside; the loop bounds are a guard
// against a caller passing a degenerate one, not a real case.
export function foldInto(hz: number, band: Band): number {
  let f = hz;
  for (let i = 0; i < 16 && f < band[0]; i++) f *= 2;
  for (let i = 0; i < 16 && f > band[1]; i++) f /= 2;
  return f;
}

// Chord tones by index, wrapping upward through octaves, folded into a band.
export function chordToneHz(chord: Chord, index: number, band: Band): number {
  const n = chord.tones.length;
  const i = ((index % n) + n) % n;
  const octave = Math.floor(index / n);
  return foldInto(MUSIC_ROOT_HZ * chord.root * chord.tones[i]! * 2 ** octave, band);
}

// ---------------------------------------------------------------------------
// Form. Sixteen steps to the bar, four bars to a phrase; patterns are step
// indices within a bar, except the hook, which is written across the phrase so
// a melodic idea can breathe over more than one chord.
export const STEPS_PER_BAR = 16;
export const BEATS_PER_BAR = 4;
export const STEPS_PER_BEAT = STEPS_PER_BAR / BEATS_PER_BAR;
export const BARS_PER_PHRASE = 4;
export const STEPS_PER_PHRASE = STEPS_PER_BAR * BARS_PER_PHRASE;

const HAT_8 = [0, 2, 4, 6, 8, 10, 12, 14] as const;
const HAT_16 = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;
const HAT_SWING = [0, 2, 3, 4, 6, 8, 10, 11, 12, 14] as const;

export type Theme = {
  id: string;
  bpm: number;
  progression: readonly ChordName[];
  kick: readonly number[];
  snare: readonly number[];
  hat: readonly number[];
  bass: readonly number[];
  pluck: readonly number[];
  /** Steps across the whole phrase, not the bar. */
  hook: readonly number[];
  /** Chord-tone index per hook step, same length as `hook`. */
  hookDegrees: readonly number[];
  bassWave: OscillatorType;
  pluckWave: OscillatorType;
};

// Five arrangements rather than five songs: same instrument set, same register
// budget, different tempo, harmony and rhythm. A run picks one from its seed,
// so the variety a player hears across ten runs is the seed's, not a playlist's.
export const THEMES: readonly Theme[] = [
  {
    id: "hollow",
    bpm: 124,
    progression: ["C", "G", "Am", "F"],
    kick: [0, 4, 8, 12],
    snare: [4, 12],
    hat: HAT_8,
    bass: [0, 3, 6, 8, 11, 14],
    pluck: [2, 6, 10, 14],
    hook: [0, 6, 12, 16, 22, 28, 40, 52],
    hookDegrees: [2, 1, 0, 1, 2, 3, 2, 4],
    bassWave: "sawtooth",
    pluckWave: "triangle",
  },
  {
    id: "chute",
    bpm: 132,
    progression: ["Am", "F", "C", "G"],
    kick: [0, 6, 8, 14],
    snare: [4, 12],
    hat: HAT_16,
    bass: [0, 2, 4, 6, 8, 10, 12, 14],
    pluck: [3, 7, 11, 15],
    hook: [0, 4, 10, 16, 20, 26, 36, 48],
    hookDegrees: [0, 1, 2, 3, 2, 1, 3, 4],
    bassWave: "sawtooth",
    pluckWave: "triangle",
  },
  {
    id: "cornice",
    bpm: 128,
    progression: ["C", "F", "G", "F"],
    kick: [0, 4, 8, 10, 12],
    snare: [4, 12],
    hat: HAT_SWING,
    bass: [0, 3, 8, 11],
    pluck: [2, 5, 6, 10, 13, 14],
    hook: [2, 8, 14, 18, 24, 30, 42, 54],
    hookDegrees: [1, 2, 3, 2, 1, 0, 2, 3],
    bassWave: "triangle",
    pluckWave: "triangle",
  },
  {
    id: "whiteout",
    bpm: 136,
    progression: ["Am", "G", "F", "G"],
    kick: [0, 3, 8, 11],
    snare: [4, 12],
    hat: HAT_8,
    bass: [0, 4, 8, 12],
    pluck: [1, 4, 7, 9, 12, 15],
    hook: [0, 8, 16, 20, 24, 28, 44, 56],
    hookDegrees: [3, 2, 1, 2, 3, 4, 3, 5],
    bassWave: "sawtooth",
    pluckWave: "square",
  },
  {
    id: "summit",
    bpm: 140,
    progression: ["C", "Em", "F", "G"],
    kick: [0, 4, 8, 12],
    snare: [4, 12, 15],
    hat: HAT_16,
    bass: [0, 2, 5, 8, 10, 13],
    pluck: [2, 4, 6, 10, 12, 14],
    hook: [4, 10, 16, 22, 26, 30, 46, 58],
    hookDegrees: [0, 2, 1, 3, 2, 4, 3, 5],
    bassWave: "sawtooth",
    pluckWave: "triangle",
  },
];

// The seed is hashed rather than taken modulo: run seeds come from the course
// generator, and a low-order bit pattern there would map straight onto a
// theme and make the choice correlate with the hill.
export function themeForSeed(seed: number): Theme {
  const r = mulberry32(seed >>> 0)();
  return THEMES[Math.min(THEMES.length - 1, Math.floor(r * THEMES.length))]!;
}

export function stepDurationS(bpm: number): number {
  return 60 / bpm / STEPS_PER_BEAT;
}

export function chordForStep(theme: Theme, step: number): Chord {
  const bar = Math.floor(step / STEPS_PER_BAR);
  const n = theme.progression.length;
  return CHORDS[theme.progression[((bar % n) + n) % n]!];
}

// A four-bar phrase repeats about eight times in a run, which is enough for an
// ear to start hearing the seam. The last bar of every second phrase takes a
// fill instead, so the eight-bar unit resolves rather than restarts.
export const FILL_STEPS: readonly number[] = [13, 14, 15];

export function isFillStep(step: number): boolean {
  if (Math.floor(step / STEPS_PER_PHRASE) % 2 !== 1) return false;
  if (Math.floor(step / STEPS_PER_BAR) % BARS_PER_PHRASE !== BARS_PER_PHRASE - 1) return false;
  return FILL_STEPS.includes(step % STEPS_PER_BAR);
}

// ---------------------------------------------------------------------------
// Vertical arrangement. The composition never stops; layers arrive and leave.
// The thresholds are fractions of the HILL, not of the clock: measured runs
// span 47 to 121 seconds, so a wall-clock arc would put a novice in the same
// section for a minute and a half and never let a fast rider reach the last one.
export type LayerGains = { pulse: number; groove: number; momentum: number; charge: number };

export const LAYER_ENTRY = {
  pulse: [0, 0.02],
  groove: [0.03, 0.1],
  momentum: [0.45, 0.58],
  charge: [0.82, 0.9],
} as const;

function ramp(x: number, a: number, b: number): number {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

// `recovery` scales only the top two layers, so a crash costs the player the
// music's momentum and it has to be rebuilt -- the same thing the pickup
// ladder does, said with arrangement instead of pitch.
export function layerGains(progress: number, recovery: number): LayerGains {
  const p = clamp01(progress);
  const r = clamp01(recovery);
  return {
    pulse: ramp(p, LAYER_ENTRY.pulse[0], LAYER_ENTRY.pulse[1]),
    groove: ramp(p, LAYER_ENTRY.groove[0], LAYER_ENTRY.groove[1]),
    momentum: ramp(p, LAYER_ENTRY.momentum[0], LAYER_ENTRY.momentum[1]) * r,
    charge: ramp(p, LAYER_ENTRY.charge[0], LAYER_ENTRY.charge[1]) * r,
  };
}

// One number for "how loud is the music trying to be", used to set the bus and
// to keep the design honest: the ride starts around two thirds and only reaches
// full on the last stretch, so the descent has somewhere to build to.
export function intensity01(g: LayerGains): number {
  return clamp01(g.pulse * 0.3 + g.groove * 0.3 + g.momentum * 0.2 + g.charge * 0.2);
}

export const MOMENTUM_RECOVERY_S = 3.2;

export function recovery01(sinceCrashS: number): number {
  if (!Number.isFinite(sinceCrashS)) return 1;
  return clamp01(sinceCrashS / MOMENTUM_RECOVERY_S);
}

// The bus opens as the arrangement fills out: warm and a little distant while
// only the low layers are in, fully present once the charge layer lands. Driven
// by intensity rather than by progress so it tracks what is actually playing --
// a run that crashed its momentum away sounds as dark as it is thin.
export function busToneHz(intensity: number): number {
  return 5000 + 13000 * ramp(clamp01(intensity), 0.6, 1);
}

// Speed is a nudge on top of the arc, not a second arc. A slow, careful run is
// still allowed to reach full intensity by the bottom of the hill.
export function speedTrim(speed01: number): number {
  return 0.9 + 0.1 * clamp01(speed01);
}
